import React from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import { CtaBtn, DangerAlert, WarningAlert } from '@tmapy/style-guide';

import { DatasetDatasourceResponse } from '../../openapi/types';
import { HarvestStatesEnum } from '../../openapi/types/HarvestStatesEnum';
import { DATASOURCE_STUCK_AFTER_MINUTES, isHarvestingEnabled } from '../datasourceHarvest';
import { useDatasourceHarvest } from '../useDatasourceHarvest';

import { datasourceStateMessages, datasourceMessages } from './datasourceMessages';

const DATASOURCE_LIST_COLUMN_COUNT = 7;

type DatasourceListItemProps = DatasetDatasourceResponse & {
  isStuck: boolean;
  refresh(): Promise<void>;
};

export const DatasourceListItem: React.FC<DatasourceListItemProps> = ({
  id,
  state,
  name,
  datasets_count,
  harvested,
  created,
  edited,
  isStuck,
  refresh,
}) => {
  const formatMessage = useMessage();

  const { harvest, isPending, hasFailed, dismissFailure } = useDatasourceHarvest(id, refresh);

  const displayedState = isPending ? HarvestStatesEnum.WAITING_FOR_PROCESSING : state;

  return (
    <>
      <tr>
        <td className='sg-u-type-bold'>{name}</td>
        <td className='sg-a-fvn-tn sg-a-ta-r'>{datasets_count}</td>
        <td className='sg-a-ws-nw sg-a-fvn-tn sg-a-ta-r'>
          {created && (
            <>
              <FormattedDate value={new Date(created)} />{' '}
              <FormattedTime value={new Date(created)} />
            </>
          )}
        </td>
        <td className='sg-a-ws-nw sg-a-fvn-tn sg-a-ta-r'>
          {edited && (
            <>
              <FormattedDate value={new Date(edited)} /> <FormattedTime value={new Date(edited)} />
            </>
          )}
        </td>
        <td className='sg-a-ws-nw sg-a-fvn-tn sg-a-ta-r'>
          {harvested && (
            <>
              <FormattedDate value={new Date(harvested)} />{' '}
              <FormattedTime value={new Date(harvested)} />
            </>
          )}
        </td>
        <td>{formatMessage.lookup(datasourceStateMessages, displayedState ?? 'null')}</td>
        <td>
          <CtaBtn isDisabled={!isHarvestingEnabled(displayedState)} onClick={harvest} size='sml'>
            <FormattedMessage {...datasourceMessages.harvest} />
          </CtaBtn>
        </td>
      </tr>
      {isStuck && (
        <tr className='dmx-a-cur-a'>
          <td colSpan={DATASOURCE_LIST_COLUMN_COUNT}>
            <WarningAlert>
              <FormattedMessage
                {...datasourceMessages.harvestStuck}
                values={{ minutes: DATASOURCE_STUCK_AFTER_MINUTES }}
              />
            </WarningAlert>
          </td>
        </tr>
      )}
      {hasFailed && (
        <tr className='dmx-a-cur-a'>
          <td colSpan={DATASOURCE_LIST_COLUMN_COUNT}>
            <DangerAlert onHideBtnClick={dismissFailure}>
              <FormattedMessage {...datasourceMessages.harvestFail} />
            </DangerAlert>
          </td>
        </tr>
      )}
    </>
  );
};
